import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyEditWebApiHandler_hasElectricHeatingSource,
  useRentalManagerUserPropertyEditWebApiHandler_hasGasHeatingSource,
  useRentalManagerUserPropertyEditWebApiHandler_hasHeatPumpHeatingSource,
  useRentalManagerUserPropertyEditWebApiHandler_hasOilHeatingSource,
  useRentalManagerUserPropertyEditWebApiHandler_hasPropaneButaneHeatingSource,
  useRentalManagerUserPropertyEditWebApiHandler_hasSolarHeatingSource,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import {
  HEATING_SOURCE_KEYS,
  HEATING_SOURCE_METADATA,
  type HeatingSourceKey,
} from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<HeatingSourceKey, boolean> & {
  hasPropaneButane: boolean;
};

const HeatingSystemsAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const heatingSourceTypes = listingDetails?.buildingAttributes?.heatingSourceTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasHeatPump: heatingSourceTypes?.hasHeatPump ?? false,
    hasElectric: heatingSourceTypes?.hasElectric ?? false,
    hasGas: heatingSourceTypes?.hasGas ?? false,
    hasOil: heatingSourceTypes?.hasOil ?? false,
    hasPropaneButane: heatingSourceTypes?.hasPropaneButane ?? false,
    hasSolar: heatingSourceTypes?.hasSolar ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Heating systems updated',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateHeatPump } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasHeatPumpHeatingSource,
    skipToast: true,
    meta: { context: { api: 'hasHeatPumpHeatingSource', details: { listingAlias } } },
  });

  const { mutateAsync: updateElectric } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasElectricHeatingSource,
    skipToast: true,
    meta: { context: { api: 'hasElectricHeatingSource', details: { listingAlias } } },
  });

  const { mutateAsync: updateGas } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasGasHeatingSource,
    skipToast: true,
    meta: { context: { api: 'hasGasHeatingSource', details: { listingAlias } } },
  });

  const { mutateAsync: updateOil } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasOilHeatingSource,
    skipToast: true,
    meta: { context: { api: 'hasOilHeatingSource', details: { listingAlias } } },
  });

  const { mutateAsync: updatePropaneButane } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasPropaneButaneHeatingSource,
    skipToast: true,
    meta: { context: { api: 'hasPropaneButaneHeatingSource', details: { listingAlias } } },
  });

  const { mutateAsync: updateSolar } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasSolarHeatingSource,
    skipToast: true,
    meta: { context: { api: 'hasSolarHeatingSource', details: { listingAlias } } },
  });

  const mutationMap: Record<keyof LocalState, typeof updateHeatPump> = {
    hasHeatPump: updateHeatPump,
    hasElectric: updateElectric,
    hasGas: updateGas,
    hasOil: updateOil,
    hasPropaneButane: updatePropaneButane,
    hasSolar: updateSolar,
  };

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as HeatingSourceKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = HEATING_SOURCE_METADATA[fieldName];
      await mutationMap[fieldName]({
        getParams: {
          [metadata.apiKey]: checked,
          listingAlias,
        },
      });
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Heating systems</Legend>} disabled={isInputFormDisabled}>
      {HEATING_SOURCE_KEYS.map((fieldName) => {
        const metadata = HEATING_SOURCE_METADATA[fieldName];
        return (
          <LabeledControl
            key={`heatingSourceTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(HeatingSystemsAmenities);
